import { Component, OnInit, Input } from '@angular/core';
import { ZoningCodeVersionService, ZoningCodeService } from 'src/app/modules/instance/services';
import { MatDialogConfig, MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AttachmentLink, AttachmentType } from 'src/app/shared';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { AttachmentLinkService } from 'src/app/modules/instance/services';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import * as _ from 'lodash';
import { PropertyRecordRelationType } from 'src/app/shared/enums/property-record-relation-type';

@Component({
    standalone: false,
    selector: 'app-zoning-code-version-data-tab',
    templateUrl: './zoning-code-version-data-tab.component.html',
    styleUrls: ['./zoning-code-version-data-tab.component.scss']
})
export class ZoningCodeVersionDataTabComponent implements OnInit {
    @Input()
    detailsData;

    @Input()
    attachmentType: AttachmentType;

    attachmentLinks: AttachmentLink[];

    isDisabled: boolean = false;
    isLoaded: boolean = false;

    foliosWithNotificationsHint: string = `Create new attachment CSV file with the list of Folios, codes of notifications and Zone components Ids.
   Notice that number of Folios in the result file might be greater than number of calculated Property Records.
   That is because codes of notifications and Zone components Ids are collected from all Buildings which forms Calculation Result for Property Record.
   Only basic Calculation Results were analyzed.`;
    failedFoliosHint: string = `Create new attachment CSV file with the list of unique Folios which failed to calculate.
   Notice that number of Folios in the result file might be less than number of calculated Property Records.
   That is because Folio is not unique attribute of Property Record and there can be duplicates.
   Only basic Calculation Results were analyzed.`;

    missedEntitiesCommonHint: string = `In case of Zoning Code, resulting file will contain data collected from Calculation Results of all versions of Zoning Code, found by Zoning Code Gridics Id.
   In case of Zoning Code Version, resulting file will contain data from Calculation Results related to specific Zoning Code Version. In case if there are no data, resulting file will not be created`;
    missedAbuttingPropertyRecordsHint: string = `Create new attachment CSV file with the list of unique Gridics Ids of Abutting Property Records
   that were requested but not received from GAPI. ${this.missedEntitiesCommonHint}`;
    missedAbuttingStreetsHint: string = `Create new attachment CSV file with the list of unique Gridics Ids of Abutting Streets
   that were requested but not received from GAPI. ${this.missedEntitiesCommonHint}`;
    missedPropertyRecordsTagsHint: string = `Create new attachment CSV file with the list of unique Gridics Ids of Property Records Tags
   that were requested but not received from GAPI. ${this.missedEntitiesCommonHint}`;

    PropertyRecordRelationType = PropertyRecordRelationType;

    constructor(
        private readonly zoningCodeVersionService: ZoningCodeVersionService,
        private readonly zoningCodeService: ZoningCodeService,
        private readonly attachmentLinkService: AttachmentLinkService,
        private readonly snackBar: MatSnackBar,
        private readonly dialog: MatDialog
    ) {}

    columnsToDisplay: string[] = ['File name', 'Actions'];

    ngOnInit() {
        this.getAttachments().subscribe(response => {
            this.attachmentLinks = response.data.attachments;
            this.isLoaded = true;
        });
    }

    get isZoningCodeVersionStatistics() {
        return (
            this.attachmentType === AttachmentType.ZC_VERSION_FAILED_PR_FOLIOS ||
            this.attachmentType === AttachmentType.ZC_VERSION_PROPERTIES_WITH_NOTIFICATIONS
        );
    }

    get isIndexedPropertiesListAttType() {
        return this.attachmentType === AttachmentType.ZC_INDEXED_PR_FOLIOS;
    }

    setAttachments(attachments) {
        this.isDisabled = true;
        this.isLoaded = false;
        attachments
            .pipe(
                catchError(response => {
                    this.openSnackBar(response.error.messages ? response.error.messages[0] : null);
                    return of(null);
                }),
                filter(data => !!data)
            )
            .subscribe(response => {
                if (!response.data.attachments.length) {
                    this.snackBar.open(`No attachments available`, 'OK', {
                        verticalPosition: 'top',
                        horizontalPosition: 'center',
                        duration: 2000
                    });
                } else {
                    this.attachmentLinks = response.data.attachments;
                }
                this.isLoaded = true;
                this.isDisabled = false;
            });
    }

    getIndexedFoliosAttachments() {
        const attachments = this.isIndexedPropertiesListAttType
            ? this.zoningCodeService.getIndexedFoliosAttachment(this.detailsData.zoningCode.Id)
            : of(null);

        this.setAttachments(attachments);
    }

    getFailedFoliosAttachments() {
        const attachments = this.isZoningCodeVersionStatistics
            ? this.zoningCodeVersionService.getFailedFoliosAttachment(this.detailsData.zoningCodeVersion.Id)
            : of(null);

        this.setAttachments(attachments);
    }

    getPropertiesWithNotificationsAttachments() {
        const attachments = this.isZoningCodeVersionStatistics
            ? this.zoningCodeVersionService.getPropertiesWithNotificationsAttachment(
                  this.detailsData.zoningCodeVersion.Id
              )
            : of(null);

        this.setAttachments(attachments);
    }

    getAttachments() {
        if (this.isIndexedPropertiesListAttType) {
            return this.zoningCodeService.getAttachmentLinksForZC(this.detailsData.zoningCode.Id);
        }

        if (this.isZoningCodeVersionStatistics) {
            return this.zoningCodeVersionService.getAttachmentLinksForZCVersion(this.detailsData.zoningCodeVersion.Id);
        }
    }

    deleteAttachment(attachmentLinkId: number, attachmentLinkFileName: string) {
        const dialogConfig = new MatDialogConfig();

        const title = 'Remove file';
        const topContent = `Are you sure you want to remove ${attachmentLinkFileName}? This operation can not be undone. Continue?`,
            okButtonColor = 'warn';

        dialogConfig.autoFocus = true;
        dialogConfig.data = { title, topContent, okButtonColor };
        dialogConfig.maxHeight = '600px';
        dialogConfig.maxWidth = '300px';

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, dialogConfig);
        dialogRef
            .afterClosed()
            .pipe(
                filter(isConfirm => isConfirm),
                tap(() => {
                    this.isDisabled = true;
                    this.isLoaded = false;
                }),
                switchMap(() => this.attachmentLinkService.deleteAttachmentLink(attachmentLinkId)),
                catchError(response => {
                    this.openSnackBar(response.error.messages[0]);
                    return of(null);
                }),
                filter(data => !!data)
            )
            .subscribe(response => {
                let message: string;

                if (response.data.isDeleted) {
                    this.attachmentLinks = this.attachmentLinks.filter(attachmentLink => {
                        return attachmentLink.Id !== attachmentLinkId;
                    });

                    message = `File ${attachmentLinkFileName} was deleted successfully`;
                } else {
                    message = 'Failed to delete file';
                }

                this.openSnackBar(message);
            });
    }

    private openSnackBar(message: string): void {
        this.snackBar.open(message, 'OK', {
            verticalPosition: 'top',
            horizontalPosition: 'center',
            duration: 4000
        });

        this.isDisabled = false;
        this.isLoaded = true;
    }

    generateMissedEntitiesAttachment(relationType: PropertyRecordRelationType) {
        const attachments = this.isZoningCodeVersionStatistics
            ? this.zoningCodeVersionService.generateMissedEntitiesAttachment(
                  this.detailsData.zoningCodeVersion.Id,
                  relationType
              )
            : this.zoningCodeService.generateMissedEntitiesAttachment(this.detailsData.zoningCode.Id, relationType);

        this.setAttachments(attachments);
    }

    generateMissedAbuttingPRsAttachment() {
        this.generateMissedEntitiesAttachment(PropertyRecordRelationType.ABUTTING_PR);
    }

    generateMissedAbuttingStreetsAttachment() {
        this.generateMissedEntitiesAttachment(PropertyRecordRelationType.ABUTTING_STREET);
    }

    generateMissedPRsTagsAttachment() {
        this.generateMissedEntitiesAttachment(PropertyRecordRelationType.PR_TAG);
    }
}
